import { useEffect, useRef, useState } from "react";
import { Bookmark, ChevronDown } from "lucide-react";
import { EXECUTIVE_FILTER_FIELDS } from "../../data/executiveFilters";
import type { FilterFieldConfig } from "./FiltersBar";

const FILTER_DEFAULTS = Object.fromEntries(EXECUTIVE_FILTER_FIELDS.map((f) => [f.key, f.options[0]]));

interface FilterPillRowProps {
  fields: FilterFieldConfig[];
  values: Record<string, string>;
  openKey: string | null;
  labelClassName: string;
  chevronSize: number;
  onToggle: (key: string) => void;
  onSelect: (key: string, value: string) => void;
}

function FilterPillRow({ fields, values, openKey, labelClassName, chevronSize, onToggle, onSelect }: FilterPillRowProps) {
  return (
    <div className="si-filters__row">
      {fields.map(({ key, label, options }) => {
        const isOpen = openKey === key;
        return (
          <div key={key} className="si-filter-pill-wrap">
            <button
              type="button"
              className={`si-filter-pill${isOpen ? " si-filter-pill--open" : ""}`}
              aria-haspopup="listbox"
              aria-expanded={isOpen}
              onClick={() => onToggle(key)}
            >
              <span className="si-filter-pill__text">
                <span className={labelClassName}>{label}:</span>
                <span className="si-filter-pill__value"> {values[key]}</span>
              </span>
              <ChevronDown size={chevronSize} className="si-filter-pill__chevron" />
            </button>
            {isOpen && (
              <div className="si-filter-menu" role="listbox" aria-label={label}>
                {options.map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    role="option"
                    aria-selected={opt === values[key]}
                    className={`si-filter-menu__item${opt === values[key] ? " si-filter-menu__item--active" : ""}`}
                    onClick={() => onSelect(key, opt)}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

interface Props {
  /** Class for the "Label:" text of each pill; lets a page keep its own label colour. */
  labelClassName?: string;
  chevronSize?: number;
  savedViewsIcon?: "bookmark" | "chevron";
  clearLabel?: string;
}

/** Filters panel shared by the Spend, Supplier & Market and Opportunity & Action pages. */
export function SpendFilterSection({
  labelClassName = "si-filter-pill__label",
  chevronSize = 10,
  savedViewsIcon = "chevron",
  clearLabel = "✕ Clear",
}: Props) {
  const [filters, setFilters] = useState<Record<string, string>>(FILTER_DEFAULTS);
  const [openKey, setOpenKey] = useState<string | null>(null);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (sectionRef.current && !sectionRef.current.contains(e.target as Node)) setOpenKey(null);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const rowProps = {
    values: filters,
    openKey,
    labelClassName,
    chevronSize,
    onToggle: (key: string) => setOpenKey((prev) => (prev === key ? null : key)),
    onSelect: (key: string, value: string) => {
      setFilters((prev) => ({ ...prev, [key]: value }));
      setOpenKey(null);
    },
  };

  return (
    <section className="si-filters" ref={sectionRef}>
      <div className="si-filters__header">
        <p className="si-filters__title">FILTERS</p>
        <div className="si-filters__actions">
          <button type="button" className="si-filters__btn">
            {savedViewsIcon === "bookmark" ? <Bookmark size={12} /> : <ChevronDown size={12} />}
            <span>Saved Views</span>
          </button>
          <button
            type="button"
            className="si-filters__btn"
            onClick={() => {
              setFilters(FILTER_DEFAULTS);
              setOpenKey(null);
            }}
          >
            {clearLabel}
          </button>
        </div>
      </div>
      <FilterPillRow fields={EXECUTIVE_FILTER_FIELDS.slice(0, 6)} {...rowProps} />
      <FilterPillRow fields={EXECUTIVE_FILTER_FIELDS.slice(6)} {...rowProps} />
    </section>
  );
}
